import { CompleteAccountInput } from "@pages/complete-account/complete-account-input";
import { UlmButton } from "@components/ulm/ulm-button";
import { Box } from "@mui-wrapper";
import { useCallback, useState } from "react";
import { AccountData } from "@pages/complete-account/complete-account-types";
import { validateTextInput } from "@pages/complete-account/complete-account-utils";

interface Props {
  /** Initial state of the account data */
  accountData: AccountData;

  /** Whether the component should be displayed in loading state */
  isLoading: boolean;

  /** Callback to update the account data */
  updateAccountData: (accountData: AccountData) => Promise<void>;
}

/** Form to complete the missing account information */
export function CompleteAccountForm({
  accountData,
  isLoading,
  updateAccountData,
}: Props): JSX.Element {
  const [firstName, setFirstName] = useState<string>(accountData.name);
  const [lastName, setLastName] = useState<string>(accountData.lastName);
  const [phoneNumber, setPhoneNumber] = useState<string>(
    accountData.phone ?? ""
  );
  const [companyName, setCompanyName] = useState<string>(
    accountData.companyName ?? ""
  );
  const [jobTitle, setJobTitle] = useState<string>(accountData.jobTitle ?? "");

  const [isFirstNameError, setIsFirstNameError] = useState<boolean>(false);
  const [isLastNameError, setIsLastNameError] = useState<boolean>(false);
  const [isPhoneNumberError, setIsPhoneNumberError] = useState<boolean>(false);
  const [isCompanyNameError, setIsCompanyNameError] = useState<boolean>(false);
  const [isJobTitleError, setIsJobTitleError] = useState<boolean>(false);

  const isFirstNameValid = validateTextInput(firstName);
  const isLastNameValid = validateTextInput(lastName);
  const isPhoneNumberValid = validateTextInput(phoneNumber);
  const isCompanyNameValid = validateTextInput(companyName);
  const isJobTitleValid = validateTextInput(jobTitle);

  const isFormValid =
    isFirstNameValid &&
    isLastNameValid &&
    isPhoneNumberValid &&
    isCompanyNameValid &&
    isJobTitleValid;

  const setFormErrors = useCallback(() => {
    setIsFirstNameError(!isFirstNameValid);
    setIsLastNameError(!isLastNameValid);
    setIsPhoneNumberError(!isPhoneNumberValid);
    setIsCompanyNameError(!isCompanyNameValid);
    setIsJobTitleError(!isJobTitleValid);
  }, [
    isCompanyNameValid,
    isFirstNameValid,
    isJobTitleValid,
    isLastNameValid,
    isPhoneNumberValid,
  ]);

  const onClick = useCallback(() => {
    // If not all fields of the form are valid then only set
    // each input not validated in error state
    if (!isFormValid) {
      return setFormErrors();
    }

    // If all fields are validate then invoke callback to update the
    // account data
    void updateAccountData({
      mailAddress: accountData.mailAddress,
      name: firstName,
      lastName,
      phone: phoneNumber,
      companyName,
      jobTitle,
    });
  }, [
    accountData,
    companyName,
    firstName,
    isFormValid,
    jobTitle,
    lastName,
    phoneNumber,
    setFormErrors,
    updateAccountData,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: "20px",
      }}
    >
      {!validateTextInput(accountData.name) && (
        <CompleteAccountInput
          title="First name"
          value={firstName}
          type="text"
          name="firstName"
          autoComplete="given-name"
          isValidValue={isFirstNameValid}
          isError={isFirstNameError}
          placeholder="Enter your first name"
          isDisabled={isLoading}
          onChange={(event) => {
            event.stopPropagation();
            setIsFirstNameError(false);
            setFirstName(event.target.value);
          }}
        />
      )}
      {!validateTextInput(accountData.lastName) && (
        <CompleteAccountInput
          title="Last name"
          value={lastName}
          type="text"
          name="lastName"
          autoComplete="family-name"
          isValidValue={isLastNameValid}
          isError={isLastNameError}
          placeholder="Enter your last name"
          isDisabled={isLoading}
          onChange={(event) => {
            event.stopPropagation();
            setIsLastNameError(false);
            setLastName(event.target.value);
          }}
        />
      )}
      {!validateTextInput(accountData.phone) && (
        <CompleteAccountInput
          title="Phone number"
          value={phoneNumber}
          type="tel"
          name="phone"
          autoComplete="tel"
          isValidValue={isPhoneNumberValid}
          isError={isPhoneNumberError}
          placeholder="Enter your phone number"
          isDisabled={isLoading}
          onChange={(event) => {
            event.stopPropagation();
            setIsPhoneNumberError(false);
            setPhoneNumber(event.target.value);
          }}
        />
      )}
      {!validateTextInput(accountData.companyName) && (
        <CompleteAccountInput
          title="Company name"
          value={companyName}
          type="text"
          name="company"
          autoComplete="organization"
          isValidValue={isCompanyNameValid}
          isError={isCompanyNameError}
          placeholder="Enter your company name"
          isDisabled={isLoading}
          onChange={(event) => {
            event.stopPropagation();
            setIsCompanyNameError(false);
            setCompanyName(event.target.value);
          }}
        />
      )}
      {!validateTextInput(accountData.jobTitle) && (
        <CompleteAccountInput
          title="Job title"
          value={jobTitle}
          type="text"
          name="jobTitle"
          autoComplete="organization-title"
          isValidValue={isJobTitleValid}
          isError={isJobTitleError}
          placeholder="Enter your job title"
          isDisabled={isLoading}
          onChange={(event) => {
            event.stopPropagation();
            setIsJobTitleError(false);
            setJobTitle(event.target.value);
          }}
        />
      )}
      <UlmButton
        isLoading={isLoading}
        onClick={onClick}
        sx={{
          marginTop: "20px",
        }}
      >
        Complete
      </UlmButton>
    </Box>
  );
}
